"use client";

import { useEffect, useState } from "react";
import { Menu, Film } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-12 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1 lg:gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-foreground font-default text-[0.95rem] font-medium tracking-[0.04em] bg-transparent hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span aria-hidden="true" className="pointer-events-none absolute left-0 -bottom-1 h-[1.5px] w-full origin-left scale-x-0 bg-primary transition-transform duration-[260ms] ease-out group-hover:scale-x-100" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onLinkClick
}: {
  onLinkClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-6">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-36 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onLinkClick} className="text-foreground font-serif text-[1.5rem] leading-tight font-medium tracking-[-0.01em] py-2 border-b border-dashed border-border hover:text-primary transition-colors duration-200">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1394" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-[250ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-background"}`}>
      <div className="relative mx-auto max-w-[1280px] px-5 md:px-8 py-5 md:py-6">
        <div className="grid grid-cols-12 items-center gap-4">
          <div className="col-span-8 md:col-span-9 flex items-center gap-6 lg:gap-10">
            <Link to="/" className="flex items-center gap-2 shrink-0 block neon-blue-outline-text" aria-label="Ruchnii Studios home">
              <WvcLogo className="h-8 w-auto" />
            </Link>

            <div className="hidden md:block">
              <MenuProvider menu_id="16" className="header-menu">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-3">
            <Link to="/about" className="hidden md:inline-flex">
  <Button variant="ghost" className="px-7 py-5 h-auto text-[0.95rem]">
    <span>
      About Us
    </span>
  </Button>
</Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="md:hidden h-11 w-11 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background text-foreground w-[88%] sm:max-w-sm border-l border-border p-0 flex flex-col">
                <SheetHeader className="px-6 pt-8 pb-4">
                  <SheetTitle className="font-serif font-medium text-foreground text-[1.75rem] tracking-[-0.015em]">
                    Ruchnii Studios
                  </SheetTitle>
                  <span className="inline-block self-start mt-2 bg-primary text-primary-foreground font-mono text-[0.7rem] font-medium uppercase tracking-[0.16em] px-2.5 py-1">
                    Navigation
                  </span>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto pt-4">
                  <MenuProvider menu_id="16" className="mobile-menu">
                    <MobileMenuConsumer onLinkClick={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="px-6 mt-6">
                    <SheetClose asChild>
                      <Link to="/about" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full text-black font-default font-bold py-6 h-auto text-base shadow-md hover:bg-primary/90 md:text-primary-foreground block">
                          About Us
                        </Button>
                      </Link>
                    </SheetClose>
                  </div>
                </div>

                <div className="relative px-6 pb-8 pt-4 mt-2">
                  <div className="flex items-end justify-center gap-3">
                    <div className="relative">
                      <div className="absolute -inset-3 bg-secondary/20" />
                      <div className="absolute -right-4 -top-2 h-8 w-8 bg-primary/50" />
                      <div className="relative flex h-20 w-20 items-center justify-center bg-card border border-border shadow-md">
                        <Film className="h-9 w-9 text-foreground" />
                      </div>
                    </div>
                    <div className="font-serif italic text-foreground text-lg leading-tight">
                      Create your story.
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className={`pointer-events-none absolute inset-x-5 md:inset-x-8 bottom-0 transition-opacity duration-[250ms] ${scrolled ? "opacity-100" : "opacity-0"}`} aria-hidden="true">
          <div className="relative h-px">
            <div className="absolute inset-0 border-t border-dashed border-border" />
            <div className="absolute top-1/2 left-[25%] -translate-x-1/2 -translate-y-1/2">
              <div className="flex items-center justify-center h-3.5 w-3.5 ring-2 ring-primary bg-background">
                <div className="h-2 w-2 bg-primary" />
              </div>
            </div>
            <div className="absolute top-1/2 left-[75%] -translate-x-1/2 -translate-y-1/2">
              <div className="flex items-center justify-center h-3.5 w-3.5 ring-2 ring-primary bg-background">
                <div className="h-2 w-2 bg-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}